import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useRoute } from "wouter";

import { api } from "../../lib/api";
import type { TaxonomyPayload } from "../../lib/api";
import { useAuth } from "../../lib/auth";
import { usePostingDraft } from "../../lib/usePostingDraft";
import { emptyLocationDraft } from "../../components/LocationPicker";

import { safeReturnTo } from "../../lib/postNavigation";
import { useSafeSearch } from "../../lib/router-safe";
import "../../components/CompanyManagementMobile.css";
import { STEPS, COMPANY_FLOW_VERSION, Form, EMPTY, ProfileIssue, upgradeCompanyDraft } from "./model";
export function useCompanyWizardDraft() {

  const [, editParams] = useRoute("/company/:id/edit");
  const editId = editParams?.id || "";
  const [, navigate] = useLocation();
  const search = useSafeSearch();
  const returnTo = editId ? "/dashboard" : safeReturnTo(new URLSearchParams(search).get("returnTo"));
  const { user, loading: authLoading, setUser, refresh } = useAuth();

  const [tax, setTax] = useState<TaxonomyPayload | null>(null);
  const [form, setForm] = useState<Form>(EMPTY);
  const [companyId, setCompanyId] = useState<string>(editId);
  const [step, setStep] = useState(0);
  const [mobileViewport, setMobileViewport] = useState(() => window.matchMedia("(max-width: 639px)").matches);
  useEffect(() => {
    const viewport = window.matchMedia("(max-width: 639px)");
    const update = () => setMobileViewport(viewport.matches);
    viewport.addEventListener("change", update);
    return () => viewport.removeEventListener("change", update);
  }, []);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [enabling, setEnabling] = useState(false);
  const [subtypeOther, setSubtypeOther] = useState(""); // v3.9.67 (QA #2) — "Other — specify" value
  const [savedSubtype, setSavedSubtype] = useState({ organizationType: "", subtype: "" });
  const [legacyExtras, setLegacyExtras] = useState(false);
  const [logoFile, setLogoFile] = useState<File | null>(null); // v3.9.69 (QA #62) — company logo/image
  const [logoUploaded, setLogoUploaded] = useState(false);
  const [coverFile, setCoverFile] = useState<File | null>(null);
  const [coverUploaded, setCoverUploaded] = useState(false);
  const [coverRemoved, setCoverRemoved] = useState(false);
  const [existingCoverUrl, setExistingCoverUrl] = useState("");
  const [proofType, setProofType] = useState("");
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [proofSubmissionId, setProofSubmissionId] = useState("");
  const [needsProof, setNeedsProof] = useState(true);
  const [proofError, setProofError] = useState<string | null>(null);
  const submitting = useRef(false);
  const [issue, setIssue] = useState<ProfileIssue | null>(null);
  const section = useRef<HTMLElement>(null);
  const saving = useRef(false);

  // [business.media](../../../../docs/product/businesses.md#business-media): replacing a logo on Review must upload that new file on the
  // next explicit save, even if an earlier step already uploaded the old logo.
  const chooseLogo = (file: File | null) => { setLogoFile(file); setLogoUploaded(false); };
  const chooseCover = (file: File | null) => {
    setCoverFile(file); setCoverUploaded(false);
    setCoverRemoved(!file && Boolean(existingCoverUrl));
  };
  const [locationDraft, setLocationDraft] = useState(emptyLocationDraft);
  const [pendingTokens, setPendingTokens] = useState({ secondaryIndustries: "", productsServices: "", customerMarkets: "" });
  const snapshot = { flowVersion: COMPANY_FLOW_VERSION, form, companyId, step, subtypeOther, legacyExtras, logoUploaded, coverUploaded, coverRemoved, existingCoverUrl, proofType, proofSubmissionId, locationDraft, pendingTokens };
  const files = useMemo(() => ({ images: coverFile ? [coverFile] : [], previousImages: [] as File[], documents: proofFile ? [proofFile] : [], logo: logoFile ? [logoFile] : [] }), [logoFile, coverFile, proofFile]);
  const draft = usePostingDraft({
    actor: String(user?.id ?? "guest"), context: user?.activeContext, type: "company", enabled: !editId && Boolean(user),
    template: { ...snapshot, form: EMPTY }, snapshot, files,
    upgrade: upgradeCompanyDraft,
    valid: value => value.flowVersion === COMPANY_FLOW_VERSION && Number.isInteger(value.step) && value.step >= 0 && value.step < STEPS.length,
    restore: (value, restored, missing) => {
      setForm(value.form); setCompanyId(value.companyId); setStep(missing ? 0 : value.step);
      setLegacyExtras(value.legacyExtras || restored.images.length > 0 || value.coverUploaded);
      if (value.companyId) setSavedSubtype({ organizationType: value.form.organizationType, subtype: value.form.subtype });
      setSubtypeOther(value.subtypeOther); setLogoUploaded(value.logoUploaded);
      setCoverFile(restored.images[0] || null); setCoverUploaded(value.coverUploaded);
      setCoverRemoved(value.coverRemoved); setExistingCoverUrl(value.existingCoverUrl);
      setProofType(value.proofType); setProofSubmissionId(value.proofSubmissionId); setProofFile(restored.documents[0] || null);
      setLocationDraft(value.locationDraft); setPendingTokens(value.pendingTokens); setLogoFile(restored.logo[0] || null);
    },
  });

  const set = <K extends keyof Form>(k: K, v: Form[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (issue?.field === k) setIssue(null);
  };
  useEffect(() => { section.current?.querySelector<HTMLElement>("h2")?.focus(); }, [step]);
  useEffect(() => {
    if (issue) section.current?.querySelector<HTMLElement>(`[data-company-field="${issue.field}"]`)?.focus();
  }, [issue]);

  // v3.9.0 — "Promote a Company" capability is required to create a business profile.
  const canPromote = !!user?.capabilities?.includes("promote_company");
  async function enablePromote() {
    setEnabling(true); setErr(null);
    try {
      const updated = await api.addActivity("promote_company");
      if (updated) setUser(updated); else await refresh();
    } catch (e: any) {
      setErr(e?.message || "Couldn't enable business posting right now.");
    } finally { setEnabling(false); }
  }

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const t = await api.taxonomy();
        if (!alive) return;
        setTax(t);
        if (editId) {
          const c = await api.getCompany(editId);
          const registrations = await api.companyLicenses(editId);
          if (c && alive) {
            setSavedSubtype({ organizationType: c.organizationType || "", subtype: c.subtype || "" });
            setNeedsProof(registrations.some(record => record.hasDocument && (record.status === "pending" || record.status === "approved")) === false);
            setExistingCoverUrl(c.coverObject ? `${api.companyCoverUrl(editId)}?v=${encodeURIComponent(c.coverObject)}` : "");
            setForm({
              name: c.name || "", tagline: c.tagline || "", city: c.city || "",
              organizationType: c.organizationType || "", subtype: c.subtype || "",
              ownershipClassifications: c.ownershipClassifications || [], corporateRelationship: c.corporateRelationship || "",
              enterpriseClassifications: c.enterpriseClassifications || [], operatingStatus: c.operatingStatus || "",
              profileRepresents: c.profileRepresents || "", businessRoles: c.businessRoles || [],
              primaryIndustry: c.primaryIndustry || "", psicCode: c.psicCode || "", secondaryIndustries: c.secondaryIndustries || [],
              productsServices: c.productsServices || [], customerMarkets: c.customerMarkets || [], serviceCoverage: c.serviceCoverage || "",
            });
          }
        }
      } catch (e: any) {
        setErr(e?.message || "Couldn't load the form.");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [editId]);

  const selectedType = useMemo(
    () => tax?.organizationTypes.find((t) => t.id === form.organizationType) || null,
    [tax, form.organizationType],
  );


return { editId, navigate, returnTo, user, authLoading, tax, form, companyId, setCompanyId, step, setStep, mobileViewport, busy, setBusy, err, setErr, loading, enabling, subtypeOther, setSubtypeOther, savedSubtype, legacyExtras, logoFile, setLogoFile, logoUploaded, setLogoUploaded, coverFile, coverUploaded, setCoverUploaded, coverRemoved, setCoverRemoved, existingCoverUrl, setExistingCoverUrl, proofType, setProofType, proofFile, setProofFile, proofSubmissionId, setProofSubmissionId, needsProof, proofError, setProofError, submitting, issue, setIssue, section, saving, chooseLogo, chooseCover, locationDraft, setLocationDraft, pendingTokens, setPendingTokens, snapshot, draft, set, canPromote, enablePromote, selectedType };
}
